import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/store/shared/supabase";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import { useSession } from "@/session/SessionProvider";
import { createUuid } from "@/store/shared/id";
import {
  photosFromDb,
  prioriteFromDb,
  prioriteToDb,
  statutFromDb,
  statutToDb,
} from "@/store/shared/maintenanceMapping";
import type { Intervention, InterventionInput, InterventionStatut } from "./types";

interface MaintenanceContextValue {
  interventions: Intervention[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  addIntervention: (input: InterventionInput) => Intervention;
  getIntervention: (id: string) => Intervention | undefined;
  removeIntervention: (id: string) => void;
  updateIntervention: (id: string, input: InterventionInput) => void;
  updateStatut: (id: string, statut: InterventionStatut) => void;
  interventionsByImmeuble: (immeubleId: string) => Intervention[];
  interventionsByBien: (bienId: string) => Intervention[];
  countByImmeuble: (immeubleId: string) => number;
  countByBien: (bienId: string) => number;
  stats: { total: number; nouveaux: number; enCours: number; termines: number; coutTotal: number };
}

const MaintenanceContext = createContext<MaintenanceContextValue | null>(null);

type Row = Record<string, unknown>;
const str = (value: unknown): string => (typeof value === "string" ? value : "");
const nullable = (value: string): string | null => (value && value.trim() ? value : null);
const num = (value: unknown): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

function rowToIntervention(row: Row): Intervention {
  return {
    id: str(row.id),
    immeubleId: str(row.immeuble_id),
    bienId: str(row.bien_id),
    probleme: str(row.titre),
    description: str(row.description),
    priorite: prioriteFromDb(str(row.priorite)),
    statut: statutFromDb(str(row.statut)),
    photos: photosFromDb(row.photos),
    cout: num(row.cout),
    prestataire: str(row.prestataire),
    date: str(row.date_signalement),
    createdAt: str(row.created_at),
  };
}

function inputToPayload(input: InterventionInput): Record<string, unknown> {
  return {
    immeuble_id: nullable(input.immeubleId),
    bien_id: nullable(input.bienId),
    titre: input.probleme,
    description: nullable(input.description),
    priorite: prioriteToDb(input.priorite),
    statut: statutToDb(input.statut),
    date_signalement: input.date,
    prestataire: nullable(input.prestataire),
    cout: Math.max(0, Number(input.cout) || 0),
    photos: input.photos,
  };
}

/**
 * Interventions de maintenance de l'espace Propriétaire — branché sur Supabase.
 * Supabase est la source de vérité (table `maintenances`). La RLS filtre le
 * périmètre du propriétaire authentifié. Le trigger serveur de cohérence
 * bien/immeuble n'est jamais contourné (on envoie les deux ids, la base valide).
 */
export function MaintenanceProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from("maintenances")
        .select("*")
        .order("date_signalement", { ascending: false });
      if (fetchError) throw fetchError;
      setInterventions(((data as Row[] | null) ?? []).map(rowToIntervention));
    } catch (caught) {
      setError(describeSupabaseError(caught));
      console.error("[Maintenance] chargement Supabase échoué", caught);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!session) {
      setInterventions([]);
      return;
    }
    void load();
  }, [session, load]);

  const addIntervention = useCallback(
    (input: InterventionInput) => {
      const optimistic: Intervention = { ...input, id: createUuid(), createdAt: new Date().toISOString() };
      setInterventions((prev) => [optimistic, ...prev]);

      if (!input.immeubleId && !input.bienId) {
        setInterventions((prev) => prev.filter((item) => item.id !== optimistic.id));
        dispatchDataError({
          scope: "Maintenance",
          message: "Sélectionnez un immeuble ou un bien pour l'intervention.",
        });
        return optimistic;
      }

      void (async () => {
        const { data, error: insertError } = await supabase
          .from("maintenances")
          .insert(inputToPayload(input))
          .select("id")
          .maybeSingle();
        if (insertError) {
          setInterventions((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Maintenance",
            message: "Impossible d'enregistrer l'intervention.",
            technical: describeSupabaseError(insertError),
          });
          return;
        }
        const realId = (data as Row | null)?.id;
        if (realId) {
          setInterventions((prev) =>
            prev.map((item) => (item.id === optimistic.id ? { ...item, id: String(realId) } : item))
          );
        }
        void load();
      })();

      return optimistic;
    },
    [load]
  );

  const updateIntervention = useCallback(
    (id: string, input: InterventionInput) => {
      const previous = interventions.find((item) => item.id === id);
      setInterventions((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));
      void (async () => {
        const { error: updateError } = await supabase.from("maintenances").update(inputToPayload(input)).eq("id", id);
        if (updateError) {
          if (previous) setInterventions((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Maintenance",
            message: "Impossible de modifier l'intervention.",
            technical: describeSupabaseError(updateError),
          });
          return;
        }
        void load();
      })();
    },
    [interventions, load]
  );

  const updateStatut = useCallback(
    (id: string, statut: InterventionStatut) => {
      const previous = interventions.find((item) => item.id === id);
      setInterventions((prev) => prev.map((item) => (item.id === id ? { ...item, statut } : item)));
      void (async () => {
        const { error: updateError } = await supabase
          .from("maintenances")
          .update({ statut: statutToDb(statut) })
          .eq("id", id);
        if (updateError) {
          if (previous) setInterventions((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Maintenance",
            message: "Impossible de changer le statut de l'intervention.",
            technical: describeSupabaseError(updateError),
          });
          return;
        }
        void load();
      })();
    },
    [interventions, load]
  );

  const removeIntervention = useCallback(
    (id: string) => {
      const previous = interventions.find((item) => item.id === id);
      setInterventions((prev) => prev.filter((item) => item.id !== id));
      void (async () => {
        const { error: deleteError } = await supabase.from("maintenances").delete().eq("id", id);
        if (deleteError) {
          if (previous) setInterventions((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Maintenance",
            message: "Impossible de supprimer l'intervention.",
            technical: describeSupabaseError(deleteError),
          });
          return;
        }
        void load();
      })();
    },
    [interventions, load]
  );

  const getIntervention = useCallback((id: string) => interventions.find((item) => item.id === id), [interventions]);

  const interventionsByImmeuble = useCallback(
    (immeubleId: string) => interventions.filter((item) => item.immeubleId === immeubleId),
    [interventions]
  );

  const interventionsByBien = useCallback(
    (bienId: string) => interventions.filter((item) => item.bienId === bienId),
    [interventions]
  );

  const countByImmeuble = useCallback(
    (immeubleId: string) => interventions.filter((item) => item.immeubleId === immeubleId).length,
    [interventions]
  );

  const countByBien = useCallback(
    (bienId: string) => interventions.filter((item) => item.bienId === bienId).length,
    [interventions]
  );

  const stats = useMemo(
    () => ({
      total: interventions.length,
      nouveaux: interventions.filter((item) => item.statut === "nouveau").length,
      enCours: interventions.filter((item) => item.statut === "en-cours").length,
      termines: interventions.filter((item) => item.statut === "termine").length,
      coutTotal: interventions.reduce((sum, item) => sum + (Number(item.cout) || 0), 0),
    }),
    [interventions]
  );

  const value = useMemo(
    () => ({
      interventions,
      loading,
      error,
      reload: load,
      addIntervention,
      getIntervention,
      removeIntervention,
      updateIntervention,
      updateStatut,
      interventionsByImmeuble,
      interventionsByBien,
      countByImmeuble,
      countByBien,
      stats,
    }),
    [
      interventions,
      loading,
      error,
      load,
      addIntervention,
      getIntervention,
      removeIntervention,
      updateIntervention,
      updateStatut,
      interventionsByImmeuble,
      interventionsByBien,
      countByImmeuble,
      countByBien,
      stats,
    ]
  );

  return <MaintenanceContext.Provider value={value}>{children}</MaintenanceContext.Provider>;
}

export function useMaintenance(): MaintenanceContextValue {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error("useMaintenance doit être utilisé à l'intérieur de MaintenanceProvider");
  }
  return context;
}